import { useSegmentFilterCascaded } from "@repo/baseCloud";
import { DropDownList } from "@repo/ui";

import type { Office, Segment } from "../settingsTree";

import { changePick } from "../segmentPicks";
import style from "./style.module.scss";

interface Props {
	segment: Segment;
	/** Every office, to fill in an office's segment and to know which later picks the lists still offer */
	offices: Office[];
	onChange: (segment: Segment) => void;
}

/** In cascade order: each pick narrows the lists after it */
const levels = [
	{ key: "brandId", label: "Brand", list: "brands", loaded: "brandsLoaded" },
	{ key: "channelId", label: "Channel", list: "channels", loaded: "channelsLoaded" },
	{ key: "marketerId", label: "Marketer", list: "marketers", loaded: "marketersLoaded" },
	{ key: "usStateId", label: "State", list: "states", loaded: "statesLoaded" },
	{ key: "branchId", label: "Office", list: "branches", loaded: "branchesLoaded" },
] as const;

/**
 * Single-select Brand, Channel, Marketer, State and Office dropdowns.
 * Changing one keeps the later picks that their lists still offer; picking an office fills in its own segment.
 * Renders a fragment so each dropdown takes its own cell in the filter bar.
 */
export function SegmentSelectors({ segment, offices, onChange }: Props) {
	const lists = useSegmentFilterCascaded({
		brandId: segment.brandId,
		channelId: segment.channelId,
		marketerId: segment.marketerId,
		stateId: segment.usStateId,
	});

	return (
		<>
			{levels.map((level) => (
				<DropDownList
					key={level.key}
					label={level.label}
					placeholder="None"
					values={lists[level.list]?.data ?? []}
					loaded={lists[level.loaded]}
					valueProp="id"
					textProp="value"
					value={segment[level.key] ?? null}
					clearable
					fullLength
					optionClassName={style.singleLineOption}
					changeHandler={(id: number) => onChange(changePick(segment, level.key, id, offices))}
				/>
			))}
		</>
	);
}
